import { Button, FlexBox, Table } from '@dixa/lib-shared-habla';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { CustomFormField } from '../../../../customForms.types';
import { FormValues } from '../../customFormEditPage.types';

import { CharLimitedTextInput } from './components/charLimitedTextInput/charLimitedTextInput';
import { FormFieldRows } from './components/formFieldRows/formFieldRows';
import { FieldDetailsModal } from './components/modals/fieldDetailsModal';
import styles from './formFields.module.scss';
import { FieldsListModalsContext } from './hooks/FieldsModalsContext';
import { useFormFieldsModalsState } from './hooks/useFormFieldsModalsState';

const maxCustomizationTextLength = 35;

const customizationTextValidation = {
  required: { value: true, message: 'This field is required' },
  maxLength: {
    value: maxCustomizationTextLength,
    message: `This field must not be longer that ${maxCustomizationTextLength} charecters`,
  },
} as const;

export function FormFields(): JSX.Element {
  const { control } = useFormContext<FormValues>();

  const fieldsModalsState = useFormFieldsModalsState();
  const { currentlyActiveFormFieldIndex } = fieldsModalsState;
  const anActiveFormFieldIsSelected = currentlyActiveFormFieldIndex !== null;

  const { move, append, update, remove, fields } = useFieldArray<FormValues>({
    name: 'formFields',
    control,
  });

  const formFields = fields as CustomFormField[];

  const currentlyActiveFormField: CustomFormField | null =
    anActiveFormFieldIsSelected
      ? formFields[currentlyActiveFormFieldIndex]
      : null;

  const handleSaveFieldDetails = (field: CustomFormField) => {
    const editMode = anActiveFormFieldIsSelected;

    if (editMode) {
      update(currentlyActiveFormFieldIndex, field);
    } else {
      append(field);
    }
  };

  return (
    <FieldsListModalsContext.Provider value={fieldsModalsState}>
      <FlexBox flexDirection="column" gap="400">
        <FlexBox justifyContent="start" gap="400">
          <CharLimitedTextInput
            control={control}
            label="Form submission button label"
            name="submitButtonLabel"
            maxLength={maxCustomizationTextLength}
            rules={customizationTextValidation}
            componentProps={{
              id: 'submitButtonLabel',
              placeholder: 'Text on the submit button',
              maxLength: maxCustomizationTextLength,
            }}
          />

          <CharLimitedTextInput
            control={control}
            label="Feedback after form submission"
            name="feedbackAfterSubmit"
            maxLength={maxCustomizationTextLength}
            rules={customizationTextValidation}
            componentProps={{
              id: 'feedbackAfterSubmit',
              placeholder: 'Feedback after form submission',
              maxLength: maxCustomizationTextLength,
            }}
          />
        </FlexBox>

        <FlexBox>
          <Table
            innerWrapperClassName={styles.tableWrapper}
            headerWithBackground
            framed
            emptyState={
              formFields.length === 0
                ? {
                    title: 'No fields added yet',
                    description: 'Get started by adding your first field.',
                  }
                : undefined
            }
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th width="65px" />
                <Table.Th>Form fields</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th width="150px" />
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              <FormFieldRows onRowMoved={move} onDelete={remove} />
            </Table.Tbody>
          </Table>
        </FlexBox>

        <FlexBox>
          <Button
            icon="plus"
            variant="secondary"
            type="button"
            onClick={() => fieldsModalsState.fieldDetailsModal.open(null)}
          >
            Add new field
          </Button>
        </FlexBox>

        {fieldsModalsState.fieldDetailsModal.isOpen && (
          <FieldDetailsModal
            isOpen={true}
            onClose={fieldsModalsState.fieldDetailsModal.close}
            activeFormField={currentlyActiveFormField}
            onSave={handleSaveFieldDetails}
          />
        )}
      </FlexBox>
    </FieldsListModalsContext.Provider>
  );
}
